import { tableFieldStyles } from "@prismicio/editor-ui"
import { type PluginView } from "@tiptap/pm/state"
import type { EditorView } from "@tiptap/pm/view"

import { findTableHandleElement } from "./utils"

/**
 * This plugin centralizes the logic for showing and hiding the row and column
 * handle buttons (the ones that on click open a floating menu).
 *
 * Ideally, hiding and showing the handles would be handled using only CSS,
 * but identifying the topmost cell in a column is not trivial, as the grouping
 * of cells in a column is not clearly defined in the DOM (only by colgroup),
 * and CSS cannot dynamically select it depending on the hovered cell.
 *
 * Another option was to dynamically generate hardcoded selectors, given that
 * we know the number of rows and cells, but inline styles can only contain
 * property declarations, not pseudo-classes like :hover.
 *
 * That leaves us with handling it solely in JavaScript, which is what this
 * plugin does.
 */
export class TableHandlesPluginView implements PluginView {
  private abortController = new AbortController()
  private currentRowHandle: Element | null = null
  private currentColumnHandle: Element | null = null

  constructor(private readonly view: EditorView) {
    this.update(this.view)
  }

  update(view: EditorView) {
    // we have to attach the listeners on every update, as the view constantly
    // changes and consequently the listeners disappear.
    this.attachEventListeners(view.dom)
  }

  destroy() {
    this.abortController.abort()
  }

  private attachEventListeners(view: HTMLElement) {
    if (!this.abortController.signal.aborted) this.abortController.abort()
    this.abortController = new AbortController()

    const { signal } = this.abortController
    view.querySelectorAll("table").forEach((table) => {
      table.addEventListener("mouseover", this.showCurrentHandles, { signal })
      table.addEventListener("mouseleave", this.hidePreviousHandles, { signal })
    })
  }

  private showCurrentHandles = (event: MouseEvent) => {
    try {
      const target = event.target as HTMLElement
      const table = event.currentTarget
      assertTable(table)

      this.hidePreviousHandles()

      const cell = getClosest("cell", target)
      const rowHandle = this.getRowHandle(cell)
      const columnHandle = this.getColumnHandle(table, cell)

      this.displayHandle(rowHandle)
      this.displayHandle(columnHandle)

      this.currentColumnHandle = columnHandle
      this.currentRowHandle = rowHandle
    } catch (error) {
      // We don't want to do anything here, we only throw errors to fail the
      // hover action early. If for some reason something other than a cell is
      // hovered inside the table, we don't care about it.
    }
  }

  private hidePreviousHandles = () => {
    if (this.currentRowHandle) this.hideHandle(this.currentRowHandle)
    if (this.currentColumnHandle) this.hideHandle(this.currentColumnHandle)
  }

  private getRowHandle(cell: HTMLTableCellElement) {
    const row = getClosest("row", cell)
    const handle = findTableHandleElement({ element: row, type: "row" })
    if (!handle) throw new Error("No row handle found")
    return handle
  }

  private getColumnHandle(table: HTMLTableElement, cell: HTMLTableCellElement) {
    const nth = cell.cellIndex + 1
    const headerCell = table.querySelector(
      `tr > th:nth-child(${nth}), tr > td:nth-child(${nth})`,
    )
    if (!headerCell) throw new Error("No header cell found")
    const handle = findTableHandleElement({
      element: headerCell,
      type: "column",
    })
    if (!handle) throw new Error("No column handle found")
    return handle
  }

  private displayHandle(handle: Element) {
    handle.classList.add(tableFieldStyles.tableHandleVisible)
  }

  private hideHandle(handle: Element) {
    handle.classList.remove(tableFieldStyles.tableHandleVisible)
  }
}

const tagNames = { cell: ["TD", "TH"], row: ["TR"] }
function getClosest(tag: "cell", element: HTMLElement): HTMLTableCellElement
function getClosest(tag: "row", element: HTMLElement): HTMLTableRowElement
function getClosest(tag: "cell" | "row", element: HTMLElement) {
  const tags = tagNames[tag]
  for (const tag of tags) {
    const foundElement = element.closest(tag)
    if (foundElement) return foundElement
  }
  throw new Error(`Failed to find closest ${tag}`)
}

function assertTable(
  element: HTMLElement | EventTarget | null | undefined,
): asserts element is HTMLTableElement {
  if (!element || (element as HTMLElement).tagName !== "TABLE") {
    throw new Error("Element is not a table")
  }
}
